/**
 * PipelineTimeline — REQ-167 (Bill 2026-07-23) granular per-call visibility.
 *
 * Renders CommitStatus.granularSteps: one row per Core Director call from the
 * server's pipeline snapshot, in ACTUAL execution order (honesty — the
 * post-sign program runs in snapshot order, not the 12-slot display order).
 * Plain-language labels (SPFx PhaseProgressPanel canon — never raw op names
 * in the primary line); the per-call result detail carries the SPFx-style
 * outputs the FSR asked for repeatedly: full account numbers and CIF ids,
 * Card PAN already masked server-side (**** 1234), doc/package refs.
 *
 * Raw snapshot types (AcctAdd, EFTCardAdd, …) ride a title tooltip + a
 * data-step-type attribute for support/dev use — same convention as
 * CommitProgressBar's data-step-key.
 */

import type { GranularPipelineStep } from '@retail-ops/shared-types';

const glyphFor = (state: string): { glyph: string; color: string; label: string } => {
  switch (state) {
    case 'done':
      return { glyph: '✓', color: 'var(--pcb-green, #386732)', label: 'Done' };
    case 'skipped':
      return { glyph: '⇢', color: 'var(--ink-500, #64748b)', label: 'Skipped' };
    case 'error-transient':
      return { glyph: '⟳', color: 'var(--pcb-amber, #b58900)', label: 'Retrying' };
    case 'error-permanent':
      return { glyph: '✕', color: 'var(--pcb-red, #b10505)', label: 'Failed' };
    default:
      return { glyph: '○', color: 'var(--ink-400, #94a3b8)', label: 'Pending' };
  }
};

export interface PipelineTimelineProps {
  steps: ReadonlyArray<GranularPipelineStep>;
}

export function PipelineTimeline({ steps }: PipelineTimelineProps): JSX.Element | null {
  if (!steps || steps.length === 0) return null;
  const done = steps.filter((s) => s.state === 'done' || s.state === 'skipped').length;
  // First non-terminal row is the live frontier — pulse it so the FSR sees
  // exactly which Core Director call is in flight.
  const frontierIndex = steps.findIndex(
    (s) => s.state !== 'done' && s.state !== 'skipped' && s.state !== 'error-permanent'
  );

  return (
    <section
      aria-label="Core Director activity"
      style={{
        border: '1px solid var(--line-200, var(--surface-border))',
        borderRadius: 8,
        background: 'var(--surface-0, #fff)',
        overflow: 'hidden',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--sp-4) var(--sp-5)',
          borderBottom: '1px solid var(--line-200, var(--surface-border))',
          background: 'var(--surface-50, #f8fafc)',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 14, color: 'var(--pcb-blue-dark)' }}>
          Core Director activity
        </h3>
        <span style={{ fontSize: 12, color: 'var(--ink-600, #475569)' }}>
          {done} of {steps.length} done
        </span>
      </header>
      <ol style={{ listStyle: 'none', margin: 0, padding: 'var(--sp-2) 0' }}>
        {steps.map((s, i) => {
          const g = glyphFor(s.state);
          const isFrontier = i === frontierIndex;
          return (
            <li
              key={`${s.index}-${s.type}`}
              data-step-type={s.type}
              title={s.type}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 'var(--sp-4)',
                padding: '6px var(--sp-5)',
                fontSize: 13,
                background: isFrontier ? 'var(--pcb-blue-50, #eef4fb)' : 'transparent',
              }}
            >
              <span
                aria-label={g.label}
                style={{
                  color: g.color,
                  width: 16,
                  flexShrink: 0,
                  textAlign: 'center',
                  fontWeight: 700,
                  ...(isFrontier ? { animation: 'pcb-pulse 1.2s ease-in-out infinite' } : {}),
                }}
              >
                {isFrontier && s.state === 'pending' ? '●' : g.glyph}
              </span>
              <span style={{ color: 'var(--ink-900, #0f172a)', flexShrink: 0 }}>{s.plainLabel}</span>
              {s.resultId && (
                <span
                  style={{
                    fontFamily: 'ui-monospace, Consolas, monospace',
                    color: 'var(--pcb-green, #386732)',
                    fontWeight: 600,
                  }}
                >
                  {s.resultId}
                </span>
              )}
              {s.detail && (
                <span
                  style={{
                    color:
                      s.state === 'error-permanent' || s.state === 'error-transient'
                        ? 'var(--pcb-red, #b10505)'
                        : 'var(--ink-600, #475569)',
                    fontSize: 12,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {s.detail}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export default PipelineTimeline;
